// Whether an element's content actually overflows its own box, kept live
// across resizes and content changes. A scrolling region that overflows
// must be reachable by keyboard alone (WCAG 2.1.1 / 2.1.3 -- axe's
// scrollable-region-focusable); a region that never overflows must not pick
// up a meaningless tab stop. `measure` runs after every render (content can
// change the overflow state without the wrapper's own box size changing,
// which a ResizeObserver on the wrapper alone would miss) and a
// ResizeObserver covers layout shifts between renders (a viewport resize).
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export function useOverflows<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [overflows, setOverflows] = useState(false);
  const measure = () => {
    const el = ref.current;
    // No rounding tolerance: a 1px overflow is still a real horizontal
    // scroll a keyboard user cannot reach without a tab stop (found by the
    // axe scan on .cx-diff-sample at 390px, scrollWidth 317 vs clientWidth
    // 316 -- a `+ 1` buffer here hid exactly that case).
    if (el) setOverflows(el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight);
  };
  useLayoutEffect(measure);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, overflows };
}
